import { useState, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import api, { formatCurrency, formatDate } from "@/lib/api";
import { ArrowRightLeft, CheckCircle, Plus, Clock } from "lucide-react";

export default function ReclassPage() {
  const { activeEntity, categories } = useApp();
  const [reclasses, setReclasses] = useState([]);
  const [types, setTypes] = useState({});
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    asset_id: "", reclass_type: "", to_category_id: "",
    fair_value: 0, reclass_date: new Date().toISOString().substring(0, 10), rationale: ""
  });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: r }, { data: t }, { data: a }] = await Promise.all([
        api.get("/reclassifications"),
        api.get("/reclassifications/types"),
        api.get("/assets", { params: { page_size: 1000, ...(activeEntity ? { entity_id: activeEntity.id } : {}) } })
      ]);
      setReclasses(r);
      setTypes(t);
      setAssets(a.assets || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [activeEntity]);

  const createReclass = async () => {
    try {
      const asset = assets.find(a => a.id === form.asset_id);
      await api.post("/reclassifications", {
        ...form,
        fair_value: form.fair_value || asset?.carrying_amount || 0
      });
      setShowForm(false);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const approve = async (id) => {
    if (!window.confirm("Approve this reclassification? This will update the asset and may generate a journal.")) return;
    try {
      const { data } = await api.post(`/reclassifications/${id}/approve`);
      alert(`Reclassification approved!${data.journal ? ` Journal: ${data.journal.reference}` : ""}`);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const selectedAsset = assets.find(a => a.id === form.asset_id);
  const selectedType = types[form.reclass_type] || {};

  return (
    <div className="space-y-6" data-testid="reclass-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
            <ArrowRightLeft size={24} className="inline mr-2 text-[#C8A961]" />
            Asset Reclassification
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">IAS 16 to IAS 40, IFRS 5 Held for Sale, category changes with full journal trail</p>
        </div>
        <button onClick={() => setShowForm(true)} className="stratum-btn-primary flex items-center gap-2" data-testid="new-reclass-btn">
          <Plus size={16} /> Reclassify Asset
        </button>
      </div>

      {/* Reclass Types Reference */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {Object.entries(types).map(([code, t]) => (
          <div key={code} className="stratum-card p-4">
            <h4 className="text-sm font-medium text-[#0B1020] mb-1">{t.from} → {t.to}</h4>
            <p className="text-xs text-[#6B7280]">{t.description}</p>
          </div>
        ))}
      </div>

      {/* New Reclass Form */}
      {showForm && (
        <div className="stratum-card p-6">
          <h3 className="font-display text-lg font-semibold mb-4">New Reclassification</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Asset *</label>
              <select className="stratum-input w-full" value={form.asset_id} onChange={e => setForm({...form, asset_id: e.target.value})} data-testid="reclass-asset-select">
                <option value="">Choose asset...</option>
                {assets.filter(a => a.status === "ACTIVE").map(a => (
                  <option key={a.id} value={a.id}>{a.tag} - {a.description} ({formatCurrency(a.carrying_amount)})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Reclassification Type *</label>
              <select className="stratum-input w-full" value={form.reclass_type} onChange={e => setForm({...form, reclass_type: e.target.value})} data-testid="reclass-type-select">
                <option value="">Select type...</option>
                {Object.entries(types).map(([code, t]) => (
                  <option key={code} value={code}>{t.from} → {t.to}</option>
                ))}
              </select>
              {selectedType.description && <p className="text-xs text-[#C8A961] mt-1">{selectedType.description}</p>}
            </div>
            {(form.reclass_type === "CATEGORY_CHANGE" || form.reclass_type.includes("IAS40")) && (
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Target Category</label>
                <select className="stratum-input w-full" value={form.to_category_id} onChange={e => setForm({...form, to_category_id: e.target.value})} data-testid="reclass-target-cat">
                  <option value="">Same category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            {form.reclass_type.includes("FV") && (
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Fair Value at Reclassification Date (R)</label>
                <input type="number" className="stratum-input w-full font-mono" value={form.fair_value} onChange={e => setForm({...form, fair_value: parseFloat(e.target.value) || 0})} data-testid="reclass-fv-input" />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Rationale *</label>
              <textarea className="stratum-input w-full" rows={2} value={form.rationale} onChange={e => setForm({...form, rationale: e.target.value})} data-testid="reclass-rationale-input" placeholder="Explain the reason for reclassification..." />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg border border-[#E8E2D5] text-sm">Cancel</button>
              <button onClick={createReclass} className="stratum-btn-primary" data-testid="submit-reclass-btn" disabled={!form.asset_id || !form.reclass_type}>Submit for Approval</button>
            </div>
          </div>
        </div>
      )}

      {/* History */}
      <div className="stratum-card overflow-hidden">
        <div className="p-4 border-b border-[#E8E2D5]"><h3 className="font-display text-base font-semibold">Reclassification History</h3></div>
        <table className="w-full stratum-table" data-testid="reclass-table">
          <thead><tr><th>Asset</th><th>Type</th><th>From → To</th><th className="text-right">Carrying</th><th>Date</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="text-center py-8 text-[#6B7280]">Loading...</td></tr> :
             reclasses.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-[#6B7280]">No reclassifications yet</td></tr> :
             reclasses.map(r => (
              <tr key={r.id} data-testid={`reclass-row-${r.id}`}>
                <td><span className="font-mono text-sm">{r.asset_tag}</span></td>
                <td className="text-xs">{r.reclass_type?.replace(/_/g, " ")}</td>
                <td className="text-xs">{r.from_standard} → {r.to_standard}</td>
                <td className="text-right font-mono tabular-nums text-sm">{formatCurrency(r.carrying_at_reclass)}</td>
                <td className="text-xs font-mono tabular-nums">{formatDate(r.reclass_date)}</td>
                <td>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.status === "APPROVED" ? "bg-[#065F46]/10 text-[#065F46]" : "bg-[#C8A961]/10 text-[#C8A961]"}`}>{r.status}</span>
                </td>
                <td>
                  {r.status === "PENDING" && (
                    <button onClick={() => approve(r.id)} className="px-2 py-1 text-xs rounded bg-[#065F46]/10 text-[#065F46] hover:bg-[#065F46]/20" data-testid={`approve-reclass-${r.id}`}>Approve</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
